/** @format */

import React, { useState, useCallback } from 'react';
import { GlobalWorkerOptions, getDocument, version as pdfjsVersion } from 'pdfjs-dist';
import { useAuth } from '../contexts/AuthContext';
import { usePracticeContent, PracticeContent } from '../hooks/usePracticeContent';
import { formatPdfText } from '../utils/pdfTextFormatter';
import {
  ClipboardDocumentListIcon,
  QuestionMarkCircleIcon,
  RectangleStackIcon,
  DocumentArrowUpIcon,
  PencilSquareIcon,
  ArrowDownTrayIcon,
  BookmarkIcon,
  TrashIcon,
  PlayIcon,
  AcademicCapIcon,
  ClockIcon,
  ChartBarIcon,
  ArrowPathIcon,
  SparklesIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import CertificateGenerator from '../components/CertificateGenerator';
import AIChatButton from '../components/AIChatButton';

// Assessment state interface
interface AssessmentState {
  contentId: string;
  answers: { [questionIndex: number]: number };
  currentQuestion: number;
  isCompleted: boolean;
  score?: number;
}

const PracticePage: React.FC = () => {
  // Add custom animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes slideIn {
        from { opacity: 0; transform: translateX(-20px); }
        to { opacity: 1; transform: translateX(0); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  const { currentUser } = useAuth();
  const {
    error,
    practiceContent,
    practiceHistory,
    practiceStats,
    generateContent,
    saveContent,
    updateAssessmentScore,
    clearError,
    getBookmarkedContent
  } = usePracticeContent(currentUser?.uid || '');

  const [activeTab, setActiveTab] = useState<'quiz' | 'likely_questions' | 'flashcards'>('quiz');
  const [inputType, setInputType] = useState<'text' | 'pdf'>('text');
  const [textInput, setTextInput] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [contentLength, setContentLength] = useState(5);
  const [generatedContent, setGeneratedContent] = useState<PracticeContent | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [assessmentState, setAssessmentState] = useState<AssessmentState | null>(null);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificateData, setCertificateData] = useState<any>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Tab configuration
  const tabs = [
    {
      id: 'quiz' as const,
      name: 'Quiz',
      icon: ClipboardDocumentListIcon,
      activeIcon: ClipboardDocumentListIcon,
      description: 'Generate interactive quizzes'
    },
    {
      id: 'likely_questions' as const,
      name: 'Likely Questions',
      icon: QuestionMarkCircleIcon,
      activeIcon: QuestionMarkCircleIcon,
      description: 'Predict exam questions'
    },
    {
      id: 'flashcards' as const,
      name: 'Flashcards',
      icon: RectangleStackIcon,
      activeIcon: RectangleStackIcon,
      description: 'Create study flashcards'
    }
  ];

  // Content length options
  const lengthOptions = [
    { value: 5, label: '5 items' },
    { value: 10, label: '10 items' },
    { value: 15, label: '15 items' },
    { value: 20, label: '20 items' }
  ];

  // Start assessment for quiz content
  const startAssessment = useCallback((content: PracticeContent) => {
    if (content.type !== 'quiz' || !content.content.questions) return;
    
    setAssessmentState({
      contentId: content.id,
      answers: {},
      currentQuestion: 0,
      isCompleted: false
    });
  }, []);

  // Submit answer for assessment
  const submitAnswer = useCallback((questionIndex: number, answerIndex: number) => {
    if (!assessmentState) return;
    
    setAssessmentState(prev => ({
      ...prev!,
      answers: {
        ...prev!.answers,
        [questionIndex]: answerIndex
      }
    }));
  }, [assessmentState]);

  // Complete assessment and calculate score
  const completeAssessment = useCallback(async () => {
    if (!assessmentState || !generatedContent) return;
    
    const questions = generatedContent.content.questions;
    let correctAnswers = 0;
    
    questions.forEach((question: any, index: number) => {
      if (assessmentState.answers[index] === question.correctAnswer) {
        correctAnswers++;
      }
    });
    
    const score = Math.round((correctAnswers / questions.length) * 100);
    
    setAssessmentState(prev => ({
      ...prev!,
      isCompleted: true,
      score
    }));
    
    // Update score in Firestore
    await updateAssessmentScore(assessmentState.contentId, score);
    
    // Check if eligible for certificate (70% or higher)
    if (score >= 70) {
      setCertificateData({
        score,
        totalQuestions: questions.length,
        courseName: generatedContent.title || 'Practice Quiz',
        completionDate: new Date()
      });
    }
  }, [assessmentState, generatedContent, updateAssessmentScore]);

  // Handle PDF file upload
  const handlePdfUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      clearError();
    } else {
      // We'll show this error through the existing error state from the hook
      console.error('Please select a valid PDF file');
    }
  }, [clearError]);

  // Extract text from PDF using a simpler approach
  // Extract text from PDF using PDF.js with local worker
  const extractPdfText = useCallback(async (file: File, signal?: AbortSignal): Promise<string> => {
    try {
      setIsProcessingPdf(true);
      
      // Check if it's a PDF file
      if (file.type !== 'application/pdf') {
        throw new Error('Please select a valid PDF file.');
      }
      
      // File size check (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('PDF file is too large. Please use a file smaller than 10MB.');
      }
      
      // Configure worker from CDN to avoid bundler resolution issues
      GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`;
      
      // Read file as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      
      // Load PDF document
      if (signal?.aborted) throw new Error('aborted');

      const loadingTask = getDocument({ data: arrayBuffer });
      // Attach abort: pdfjs supports cancel via destroy
      if (signal) {
        signal.addEventListener('abort', () => {
          try { (loadingTask as any)?.destroy?.(); } catch {}
        }, { once: true });
      }
      const pdf = await loadingTask.promise;
      
      let fullText = '';
      
      // Extract text from each page
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        if (signal?.aborted) throw new Error('aborted');
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        // Combine text items
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        
        fullText += pageText + '\n';
      }
      
      // Clean up extracted text using enhanced formatting
      const cleanText = formatPdfText(fullText, {
        preserveParagraphs: true,
        preserveHeaders: true,
        preserveLists: true,
        preserveTables: true,
        addSpacing: true
      });
      
      if (cleanText.length < 50) {
        throw new Error('Could not extract sufficient text from PDF. Please ensure the PDF contains readable text.');
      }
      
      return cleanText;
      
    } catch (error) {
      console.error('PDF extraction error:', error);
      
      // Provide helpful error message
      if (error instanceof Error && error.message.includes('sufficient text')) {
        throw error;
      }
      
      throw new Error('Failed to extract text from PDF. Please ensure it\'s a text-based PDF (not scanned images) or try copying and pasting the text instead.');
    } finally {
      setIsProcessingPdf(false);
    }
  }, []);

  // Generate AI content
  const handleGenerateContent = useCallback(async () => {
    if (!currentUser?.uid) {
      clearError();
      return;
    }

    let sourceContent = '';
    
    if (inputType === 'text') {
      if (!textInput.trim()) {
        return;
      }
      sourceContent = textInput.trim();
    } else {
      if (!pdfFile) {
        return;
      }
      try {
        setIsGenerating(true);
        const controller = new AbortController();
        // Cancel extraction if component unmounts or navigation occurs
        let cancelled = false;
        const cleanup = () => { if (!cancelled) { try { controller.abort(); } catch {} cancelled = true; } };
        // Attach temporary beforeunload to cancel extraction
        window.addEventListener('beforeunload', cleanup);
        sourceContent = await extractPdfText(pdfFile, controller.signal);
        window.removeEventListener('beforeunload', cleanup);
      } catch (error) {
        setIsGenerating(false);
        setLocalError('Failed to extract text from PDF. Please try again or use text input instead.');
        return;
      }
    }

    try {
      setIsGenerating(true);
      setLocalError(null);
      const result = await generateContent(activeTab, sourceContent, contentLength, inputType);
      
      if (result) {
        console.log('Generated content:', result);
        console.log('Source content used:', sourceContent);
        setGeneratedContent(result);
        // Clear inputs after successful generation
        setTextInput('');
        setPdfFile(null);
        setLocalError(null);
      } else {
        setLocalError('Failed to generate content. Please try again.');
      }
    } catch (error) {
      console.error('Error generating content:', error);
      setLocalError('An error occurred while generating content. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [currentUser?.uid, inputType, textInput, pdfFile, activeTab, contentLength, extractPdfText, generateContent, clearError]);

  // Handle save content
  const handleSaveContent = useCallback(async () => {
    if (!generatedContent) return;
    
    await saveContent(generatedContent.id);
    // You could show a toast notification here
    alert('Content saved to bookmarks!');
  }, [generatedContent, saveContent]);

  // Download content as JSON
  const downloadContent = useCallback(() => {
    if (!generatedContent) return;

    const content = JSON.stringify(generatedContent.content, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${generatedContent.title.replace(/\s+/g, '_')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [generatedContent]);

  // Clear generated content
  const clearContent = useCallback(() => {
    setGeneratedContent(null);
    setAssessmentState(null);
    clearError();
  }, [clearError]);

  // Delete functionality available for future implementation if needed

  // Render content based on type
  const renderGeneratedContent = () => {
    if (!generatedContent?.content) return null;

    const content = generatedContent.content;

    switch (activeTab) {
      case 'quiz':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Generated Quiz</h3>
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveContent}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                >
                  <BookmarkIcon className="h-4 w-4 mr-2" />
                  Save
                </button>
                <button
                  onClick={downloadContent}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Download
                </button>
                {generatedContent.type === 'quiz' && !assessmentState && (
                  <button
                    onClick={() => startAssessment(generatedContent)}
                    className="inline-flex items-center px-3 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-white hover:bg-green-50 transition-colors"
                  >
                    <PlayIcon className="h-4 w-4 mr-2" />
                    Take Quiz
                  </button>
                )}
                <button
                  onClick={clearContent}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 transition-colors"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Clear
                </button>
              </div>
            </div>
            
            {/* Assessment Mode */}
            {assessmentState && !assessmentState.isCompleted && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <PlayIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <span className="font-medium text-blue-900">Assessment Mode</span>
                  </div>
                  <span className="text-sm text-blue-700">
                    Question {Object.keys(assessmentState.answers).length + 1} of {content.questions?.length || 0}
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ 
                      width: `${Math.round((Object.keys(assessmentState.answers).length / (content.questions?.length || 1)) * 100)}%` 
                    }}
                  ></div>
                </div>
                
                <div className="mt-2 text-xs text-blue-600">
                  {Object.keys(assessmentState.answers).length} of {content.questions?.length || 0} questions answered
                </div>
              </div>
            )}
            
            {(!content.questions || content.questions.length === 0) && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                No quiz questions were generated. Please try again with more specific text or a different PDF.
              </div>
            )}
            {content.questions && content.questions.length > 0 && content.questions.map((question: any, index: number) => (
              <div 
                key={`${generatedContent?.id}-question-${index}`} 
                className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm opacity-0 animate-[slideIn_0.3s_ease-out_forwards]"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="mb-4">
                  <h4 className="text-base font-medium text-gray-900 mb-3">
                    Question {index + 1}: {question.question}
                  </h4>
                  <div className="space-y-2">
                    {!assessmentState && (
                      <div className="mb-2 text-xs text-gray-500">Click "Take Quiz" to enable answering.</div>
                    )}
                    {question.options?.map((option: string, optionIndex: number) => (
                      <label key={optionIndex} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                        <input
                          type="radio"
                          name={`question_${index}`}
                          value={optionIndex}
                          onChange={() => {
                            if (!assessmentState && generatedContent?.type === 'quiz') {
                              startAssessment(generatedContent);
                              setTimeout(() => submitAnswer(index, optionIndex), 0);
                              return;
                            }
                            if (assessmentState && !assessmentState.isCompleted) {
                              submitAnswer(index, optionIndex);
                            }
                          }}
                          disabled={assessmentState?.isCompleted}
                          checked={assessmentState ? assessmentState.answers[index] === optionIndex : false}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 cursor-pointer disabled:cursor-not-allowed"
                        />
                        <span className={`ml-3 text-sm transition-all duration-200 ${
                          assessmentState && assessmentState.answers[index] === optionIndex 
                            ? 'text-primary-700 font-medium scale-105' 
                            : 'text-gray-700'
                        }`}>
                          {option}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Show explanation after assessment is completed */}
                {assessmentState?.isCompleted && question.explanation && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-md">
                    <p className="text-sm text-blue-800">
                      <strong>Explanation:</strong> {question.explanation}
                    </p>
                    {assessmentState.answers[index] === question.correctAnswer ? (
                      <p className="text-sm text-green-600 font-medium mt-2">✓ Correct!</p>
                    ) : (
                      <p className="text-sm text-red-600 font-medium mt-2">✗ Incorrect. Correct answer: {question.options[question.correctAnswer]}</p>
                    )}
                  </div>
                )}
                
                {/* Show explanation in study mode (not assessment) */}
                {!assessmentState && question.explanation && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-md">
                    <p className="text-sm text-blue-800">
                      <strong>Explanation:</strong> {question.explanation}
                    </p>
                  </div>
                )}
              </div>
            ))}
            
            {/* Assessment Complete Button */}
            {assessmentState && !assessmentState.isCompleted && Object.keys(assessmentState.answers).length > 0 && (
              <div className="text-center mt-6">
                <button
                  onClick={completeAssessment}
                  disabled={Object.keys(assessmentState.answers).length < content.questions?.length}
                  className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white transition-colors transform hover:scale-105 transition-transform ${
                    Object.keys(assessmentState.answers).length < content.questions?.length
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {Object.keys(assessmentState.answers).length < content.questions?.length
                    ? `Answer ${content.questions?.length - Object.keys(assessmentState.answers).length} more question${content.questions?.length - Object.keys(assessmentState.answers).length === 1 ? '' : 's'}`
                    : 'Submit Quiz'
                  }
                </button>
              </div>
            )}
            
            {/* Assessment Results */}
            {assessmentState?.isCompleted && (
              <div className="mt-6 p-6 bg-gray-50 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Assessment Results</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{assessmentState.score ?? 0}%</div>
                    <div className="text-sm text-gray-600">Score</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {Object.values(assessmentState.answers).filter((answer, index) => 
                        answer === content.questions[index]?.correctAnswer
                      ).length}
                    </div>
                    <div className="text-sm text-gray-600">Correct</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {content.questions.length - Object.values(assessmentState.answers).filter((answer, index) => 
                        answer === content.questions[index]?.correctAnswer
                      ).length}
                    </div>
                    <div className="text-sm text-gray-600">Incorrect</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-600">{content.questions.length}</div>
                    <div className="text-sm text-gray-600">Total</div>
                  </div>
                </div>
                
                {/* Visual Score Chart */}
                <div className="mb-6">
                  <div className="flex items-center justify-center space-x-2 mb-3">
                    <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">Correct</span>
                    <div className="w-4 h-4 bg-red-500 rounded-full ml-4"></div>
                    <span className="text-sm text-gray-600">Incorrect</span>
                  </div>
                  
                  <div className="flex h-8 bg-gray-200 rounded-lg overflow-hidden">
                    <div 
                      className="bg-green-500 transition-all duration-1000 ease-out"
                      style={{ 
                        width: `${(Object.values(assessmentState.answers).filter((answer, index) => 
                          answer === content.questions[index]?.correctAnswer
                        ).length / content.questions.length) * 100}%` 
                      }}
                    ></div>
                    <div 
                      className="bg-red-500 transition-all duration-1000 ease-out"
                      style={{ 
                        width: `${((content.questions.length - Object.values(assessmentState.answers).filter((answer, index) => 
                          answer === content.questions[index]?.correctAnswer
                        ).length) / content.questions.length) * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
                
                {(assessmentState.score ?? 0) >= 70 ? (
                  <div className="flex items-center justify-center p-4 bg-green-50 border border-green-200 rounded-lg animate-pulse">
                    <AcademicCapIcon className="h-6 w-6 text-green-600 mr-2 animate-bounce" />
                    <span className="text-green-800 font-medium">🎉 Congratulations! You're eligible for a certificate! 🎉</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <span className="text-yellow-800">💪 You need 70% or higher to earn a certificate. Keep practicing!</span>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="text-center mt-6 space-x-3">
                  <button
                    onClick={() => setAssessmentState(null)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-2" />
                    Try Again
                  </button>
                  
                  <button
                    onClick={() => {
                      if (generatedContent) {
                        setAssessmentState({
                          contentId: generatedContent.id,
                          answers: {},
                          currentQuestion: 0,
                          isCompleted: false
                        });
                      }
                    }}
                    className="inline-flex items-center px-4 py-2 border border-primary-300 text-sm font-medium rounded-lg text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors"
                  >
                    <PlayIcon className="h-4 w-4 mr-2" />
                    Retake Quiz
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 'likely_questions':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Likely Exam Questions</h3>
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveContent}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                >
                  <BookmarkIcon className="h-4 w-4 mr-2" />
                  Save
                </button>
                <button
                  onClick={downloadContent}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Download
                </button>
                <button
                  onClick={clearContent}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 transition-colors"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Clear
                </button>
              </div>
            </div>
            
            {content.questions && content.questions.map((question: any, index: number) => (
              <div key={`${generatedContent?.id}-likely-question-${index}`} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h4 className="text-base font-medium text-gray-900 mb-3">
                  {question.question}
                </h4>
                {question.options && (
                  <div className="space-y-2 mb-4">
                    {question.options.map((option: string, optionIndex: number) => (
                      <div key={optionIndex} className="text-sm text-gray-700">
                        <span className="font-medium">{String.fromCharCode(65 + optionIndex)}.</span> {option}
                      </div>
                    ))}
                  </div>
                )}
                {question.explanation && (
                  <div className="mt-4 p-3 bg-green-50 rounded-md">
                    <p className="text-sm text-green-800">
                      <strong>Answer Rationale:</strong> {question.explanation}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case 'flashcards':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Study Flashcards</h3>
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveContent}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                >
                  <BookmarkIcon className="h-4 w-4 mr-2" />
                  Save
                </button>
                <button
                  onClick={downloadContent}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Download
                </button>
                <button
                  onClick={clearContent}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 transition-colors"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Clear
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.cards && content.cards.map((card: any, index: number) => (
                <div key={`${generatedContent?.id}-flashcard-${index}`} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="text-center">
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Front</h4>
                        <p className="mt-2 text-base font-medium text-gray-900">{card.front}</p>
                      </div>
                      <hr className="my-4" />
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Back</h4>
                        <p className="mt-2 text-sm text-gray-700">{card.back}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-x-hidden">
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 overflow-hidden">
        {/* Main Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            
            {/* Left Side - Text Content */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Welcome Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <AcademicCapIcon className="h-3 w-3 mr-1.5 text-purple-300" />
                AI-Powered Learning
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                <span className="block">Master Your</span>
                <span className="block bg-gradient-to-r from-purple-200 via-pink-200 to-indigo-200 bg-clip-text text-transparent">
                  Study Skills
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-purple-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Generate personalized quizzes, likely exam questions, and flashcards using AI. Transform any study material into interactive learning content.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2 py-3" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {practiceStats?.totalQuizzes || 0}
                  </div>
                  <div className="text-xs text-purple-200">Quizzes Taken</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {practiceStats?.averageScore || 0}%
                  </div>
                  <div className="text-xs text-purple-200">Avg Score</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {practiceStats?.totalFlashcards || 0}
                  </div>
                  <div className="text-xs text-purple-200">Flashcards</div>
                </div>
              </div>

              {/* Call-to-Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start" style={{ width: '100%', maxWidth: '100%' }}>
                <button
                  onClick={() => document.getElementById('text-input')?.focus()}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all duration-200 shadow-lg text-sm"
                >
                  <RocketLaunchIcon className="h-4 w-4 mr-1.5" />
                  Start Learning
                </button>
                <button
                  onClick={() => setActiveTab('quiz')}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                >
                  <SparklesIcon className="h-4 w-4 mr-1.5" />
                  Generate Quiz
                </button>
              </div>
            </div>

            {/* Right Side - Visual Elements - Hidden on mobile for stability */}
            <div className="relative hidden xl:block">
              {/* Practice Cards */}
              <div className="space-y-4">
                {/* Quiz Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
                      <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Smart Quizzes</h3>
                      <p className="text-purple-200 text-sm">AI-generated questions</p>
                    </div>
                  </div>
                </div>

                {/* Flashcards Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-full flex items-center justify-center">
                      <RectangleStackIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Study Cards</h3>
                      <p className="text-purple-200 text-sm">Perfect for memorization</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Main Dashboard Content */}
      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 pt-0 sm:pt-6 relative z-20 space-y-4 sm:space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 w-full">
            {/* Tabs */}
            <div className="mb-4 sm:mb-8">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
                  {tabs.map((tab) => {
                    const Icon = activeTab === tab.id ? tab.activeIcon : tab.icon;
                    return (
                      <button
                        key={`tab-${tab.id}`}
                        onClick={() => setActiveTab(tab.id)}
                        className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                          activeTab === tab.id
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon
                          className={`-ml-0.5 mr-2 h-5 w-5 transition-colors ${
                            activeTab === tab.id ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                          }`}
                        />
                        {tab.name}
                      </button>
                    );
                  })}
                </nav>
              </div>
              <div className="mt-4">
                <p className="text-sm text-gray-600">
                  {tabs.find(t => t.id === activeTab)?.description}
                </p>
              </div>
            </div>

            {/* Input Section */}
            <div className="bg-white rounded-none sm:rounded-lg shadow-sm border-0 sm:border border-gray-200 p-3 sm:p-6 mb-4 sm:mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4 sm:mb-6">Content Input</h2>
              
              {/* Input Type Toggle */}
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-6">
                <button
                  onClick={() => setInputType('text')}
                  className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
                    inputType === 'text'
                      ? 'bg-primary-50 border-primary-200 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <PencilSquareIcon className="h-5 w-5 mr-2" />
                  Text Input
                </button>
                <button
                  onClick={() => setInputType('pdf')}
                  className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
                    inputType === 'pdf'
                      ? 'bg-primary-50 border-primary-200 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                  PDF Upload
                </button>
              </div>

              {/* Text Input */}
              {inputType === 'text' && (
                <div className="mb-6">
                  <label htmlFor="text-input" className="block text-sm font-medium text-gray-700 mb-2">
                    Enter your study content
                  </label>
                  <textarea
                    id="text-input"
                    rows={8}
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder="Paste your study material, lecture notes, or any content you want to generate practice questions from..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              )}

              {/* PDF Upload */}
              {inputType === 'pdf' && (
                <div className="mb-6">
                  <label htmlFor="pdf-upload" className="block text-sm font-medium text-gray-700 mb-2">
                    Upload PDF file
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-400 transition-colors">
                    <div className="space-y-1 text-center">
                      <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="pdf-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                        >
                          <span>Upload a PDF file</span>
                          <input
                            id="pdf-upload"
                            type="file"
                            accept=".pdf"
                            onChange={handlePdfUpload}
                            className="sr-only"
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PDF up to 10MB</p>
                      {pdfFile && (
                        <div className="space-y-2">
                          <p className="text-sm text-green-600 font-medium">
                            Selected: {pdfFile.name}
                          </p>
                          <div className="flex items-center justify-center">
                            {isProcessingPdf ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                                <span className="ml-2 text-xs text-green-600">Extracting text...</span>
                              </>
                            ) : (
                              <>
                                <div className="w-4 h-4 bg-green-600 rounded-full"></div>
                                <span className="ml-2 text-xs text-green-600">Ready to extract text</span>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Content Length Selector */}
              <div className="mb-6">
                <label htmlFor="content-length" className="block text-sm font-medium text-gray-700 mb-2">
                  Number of items to generate
                </label>
                <select
                  id="content-length"
                  value={contentLength}
                  onChange={(e) => setContentLength(Number(e.target.value))}
                  className="w-full sm:w-auto border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {lengthOptions.map((option) => (
                    <option key={`length-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerateContent}
                disabled={isGenerating || (!textInput.trim() && !pdfFile)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Generating {contentLength} {activeTab === 'quiz' ? 'questions' : activeTab === 'likely_questions' ? 'questions' : 'flashcards'}...
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-5 w-5 mr-2" />
                    Generate {contentLength} {activeTab === 'quiz' ? 'questions' : activeTab === 'likely_questions' ? 'questions' : 'flashcards'}
                  </>
                )}
              </button>
              
              {/* Generation Progress */}
              {isGenerating && (
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-primary-600 h-2 rounded-full animate-pulse transition-all duration-1000 ease-out"></div>
                  </div>
                  <p className="text-xs text-gray-600 mt-1 text-center">
                    AI is analyzing your content and generating {contentLength} {activeTab === 'quiz' ? 'questions' : activeTab === 'likely_questions' ? 'questions' : 'flashcards'}...
                  </p>
                </div>
              )}
              
              {/* Success Message */}
              {generatedContent && !isGenerating && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg opacity-0 animate-[fadeIn_0.5s_ease-in_forwards]">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-5 h-5 bg-green-400 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-800">
                        <strong>Success!</strong> Generated {contentLength} {activeTab === 'quiz' ? 'questions' : activeTab === 'likely_questions' ? 'questions' : 'flashcards'} from your content.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {(error || localError) && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{localError || error}</p>
                </div>
              )}
            </div>

            {/* Generated Content */}
            {generatedContent && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                {renderGeneratedContent()}
              </div>
            )}
          </div>

          {/* Sidebar - History & Dashboard */}
          <div className="lg:col-span-1 w-full">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 sticky top-8">
              <h3 className="text-lg font-medium text-gray-900 mb-6">Practice Dashboard</h3>
              
              {/* Quick Stats */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center">
                    <ClipboardDocumentListIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-blue-900">Quizzes</span>
                  </div>
                  <span className="text-sm font-bold text-blue-900">
                    {practiceStats.totalQuizzes}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center">
                    <QuestionMarkCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                    <span className="text-sm font-medium text-green-900">Questions</span>
                  </div>
                  <span className="text-sm font-bold text-green-900">
                    {practiceStats.totalQuestions}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center">
                    <RectangleStackIcon className="h-5 w-5 text-purple-600 mr-2" />
                    <span className="text-sm font-medium text-purple-900">Flashcards</span>
                  </div>
                  <span className="text-sm font-bold text-purple-900">
                    {practiceStats.totalFlashcards}
                  </span>
                </div>
                
                {practiceStats.totalAssessments > 0 && (
                  <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <div className="flex items-center">
                      <ChartBarIcon className="h-5 w-5 text-yellow-600 mr-2" />
                      <span className="text-sm font-medium text-yellow-900">Avg Score</span>
                    </div>
                    <span className="text-sm font-bold text-yellow-900">
                      {Math.round(practiceStats.averageScore)}%
                    </span>
                  </div>
                )}
              </div>
              
              {/* Performance Chart */}
              {practiceStats.totalAssessments > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Performance Trend</h4>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-end justify-center space-x-1 h-16">
                      <div className="flex flex-col items-center">
                        <div 
                          className="w-6 bg-primary-600 rounded-t transition-all duration-500 ease-out"
                          style={{ height: `${Math.min(100, Math.max(10, practiceStats.averageScore))}%` }}
                        ></div>
                        <span className="text-xs text-gray-500 mt-1">Avg</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div 
                          className="w-6 bg-green-600 rounded-t transition-all duration-500 ease-out"
                          style={{ height: `${Math.min(100, Math.max(10, 70))}%` }}
                        ></div>
                        <span className="text-xs text-gray-500 mt-1">Goal</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent History */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Activity</h4>
                {practiceHistory.length > 0 ? (
                  <div className="space-y-3">
                    {(showHistory ? practiceHistory : practiceHistory.slice(0, 5)).map((item) => (
                      <div key={`${item.id}-${item.date.getTime()}`} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-shrink-0">
                          {item.type === 'quiz' && <ClipboardDocumentListIcon className="h-4 w-4 text-blue-600" />}
                          {item.type === 'likely_questions' && <QuestionMarkCircleIcon className="h-4 w-4 text-green-600" />}
                          {item.type === 'flashcards' && <RectangleStackIcon className="h-4 w-4 text-purple-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate">{item.title}</p>
                          <div className="flex items-center mt-1">
                            <ClockIcon className="h-3 w-3 text-gray-400 mr-1" />
                            <p className="text-xs text-gray-500">{item.date.toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {practiceHistory.length > 5 && (
                      <button
                        onClick={() => setShowHistory(!showHistory)}
                        className="w-full text-center text-xs text-primary-600 hover:text-primary-700 font-medium py-2"
                      >
                        {showHistory ? 'Show Less' : `View All (${practiceHistory.length})`}
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No practice history yet</p>
                )}
              </div>

              {/* Quick Actions */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h4>
                <div className="space-y-2">
                  <button 
                    onClick={() => setShowBookmarks(!showBookmarks)}
                    className="w-full flex items-center justify-start px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <BookmarkIcon className="h-4 w-4 mr-2" />
                    View Bookmarks ({getBookmarkedContent().length})
                  </button>
                  {showBookmarks && (
                    <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      {getBookmarkedContent().length > 0 ? (
                        <div className="space-y-2 max-h-60 overflow-auto">
                          {getBookmarkedContent().map((b) => (
                            <button
                              key={b.id}
                              onClick={() => {
                                const found = practiceContent.find(c => c.id === b.id);
                                if (found) {
                                  setGeneratedContent(found);
                                  setActiveTab(found.type);
                                  setShowBookmarks(false);
                                }
                              }}
                              className="w-full text-left p-2 bg-white rounded border border-gray-200 hover:bg-gray-50"
                            >
                              <p className="text-xs font-medium text-gray-900 truncate">{b.title}</p>
                              <p className="text-[11px] text-gray-500 mt-1">{b.type === 'quiz' ? 'Quiz' : b.type === 'likely_questions' ? 'Likely Questions' : 'Flashcards'}</p>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">No bookmarks yet</p>
                      )}
                    </div>
                  )}
                  <button className="w-full flex items-center justify-start px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                    <ChartBarIcon className="h-4 w-4 mr-2" />
                    Performance Stats
                  </button>
                  <button 
                    onClick={() => setShowCertificate(true)}
                    disabled={!certificateData}
                    className="w-full flex items-center justify-start px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <AcademicCapIcon className="h-4 w-4 mr-2" />
                    {certificateData ? 'Get Certificate' : 'Certificates (Complete Quiz First)'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Certificate Generator Modal */}
      {showCertificate && certificateData && (
        <CertificateGenerator
          assessmentData={certificateData}
          onClose={() => setShowCertificate(false)}
        />
      )}

      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default PracticePage;
